// Bloc "Actualites" des pages de services.
//
// Heritage WordPress : 19 pages se terminaient par un widget dynamique "Actualites"
// (dernier article). L'import n'en a garde que le texte d'attente, en dur dans le
// content_html : "<h2>Actualites</h2><h2>Article a la une ici</h2>** ** ** ** **".
// Ce texte reste intact en base ; il est retire a l'affichage (stripNewsPlaceholder)
// et remplace par une vraie section d'articles (pickServiceNews), choisis par
// rapprochement entre la page et les categories du blog.
//
// Module JavaScript pur : partage par le build (fetch-content), l'application Vue
// et le serveur live (articles frais).

// Titre "Actualites" + "Article a la une ici" + lignes "**", balises et espaces tolerants.
const PLACEHOLDER =
  /<h2[^>]*>\s*Actualit(?:é|e|&eacute;)s\s*<\/h2>\s*<h2[^>]*>\s*Article (?:à|a|&agrave;) la une ici\s*(?:<br\s*\/?>\s*)*<\/h2>\s*(?:<p[^>]*>\s*)?(?:\*\*\s*(?:<br\s*\/?>\s*)?)+(?:<\/p>)?/gi

/** Retire le texte d'attente du widget WordPress. Renvoie { html, found }. */
export function stripNewsPlaceholder(html) {
  if (!html || typeof html !== 'string') return { html: html || '', found: false }
  let found = false
  const out = html.replace(PLACEHOLDER, () => {
    found = true
    return ''
  })
  return { html: found ? out.replace(/\s+$/, '') + '\n' : html, found }
}

// Mots sans valeur pour rapprocher une page d'une categorie.
const STOP = new Set([
  'mayenne', 'laval', 'baule', 'evron', 'pour', 'avec', 'dans', 'votre', 'notre', 'nous',
  'mesure', 'expert', 'artisan', 'professionnel', 'professionnels', 'entreprise',
  'travaux', 'pose', 'service', 'services', 'conseils', 'ateliers', 'terras',
])

// Actions generiques : elles disent quoi faire, pas sur quel ouvrage. Poids faible.
const WEAK = new Set(['nettoy', 'entret', 'repara', 'depann', 'renova', 'instal', 'fabric'])

function stems(text) {
  return String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= 4 && !STOP.has(w))
    .map((w) => w.slice(0, 6))
}

/**
 * Articles a proposer sous une page de service : ceux de la (ou des) categorie(s) la plus
 * proche du chemin et du titre de la page, du plus recent au plus ancien, completes par
 * les articles les plus recents du blog pour toujours en proposer `limit`.
 * `articles` : schema interne (category_ids, date) ; `categories` : wp_id, slug, name.
 */
export function pickServiceNews(page, articles, categories, limit = 3) {
  // Poids des mots de la page : le premier mot du slug designe l'ouvrage (3), les autres
  // mots du slug le precisent (2), le titre complete (1), les actions generiques valent 1.
  const weights = new Map()
  const give = (stem, w) => weights.set(stem, Math.max(weights.get(stem) || 0, WEAK.has(stem) ? 1 : w))
  let first = true
  for (const s of stems((page.path || '').split('/').filter(Boolean).pop())) {
    give(s, first && !WEAK.has(s) ? 3 : 2)
    if (!WEAK.has(s)) first = false
  }
  for (const s of stems(page.title)) give(s, 1)
  const sorted = [...(articles || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''))
  const used = new Set()
  for (const a of sorted) for (const id of a.category_ids || []) used.add(id)

  // Score de chaque categorie, puis paliers du plus proche au moins proche.
  const scoreByCat = new Map()
  for (const c of categories || []) {
    if (!used.has(c.wp_id) || c.slug === 'a-classer') continue
    let score = 0
    for (const s of new Set([...stems(c.slug), ...stems(c.name)])) score += weights.get(s) || 0
    if (score > 0) scoreByCat.set(c.wp_id, score)
  }
  const tiers = [...new Set(scoreByCat.values())].sort((a, b) => b - a)

  // On remplit palier par palier (articles les plus recents d'abord dans chaque palier),
  // puis, en dernier recours seulement, avec les articles les plus recents du blog.
  const out = []
  const chosen = new Set()
  const take = (a) => {
    if (out.length < limit && !chosen.has(a.path)) {
      out.push(a)
      chosen.add(a.path)
    }
  }
  for (const tier of tiers) {
    if (out.length >= limit) break
    const ids = new Set([...scoreByCat].filter(([, s]) => s === tier).map(([id]) => id))
    for (const a of sorted) if (a.category_ids?.some((id) => ids.has(id))) take(a)
  }
  for (const a of sorted) take(a)
  return out
}
